import { CalendarDate, UserId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import {
    DateRangeControl,
    DateRangeValue,
    getDatePresets,
} from "components/reports/date-presets";
import { defaultCardStyle } from "components/ui/card";
import { SpMultiSelect } from "components/ui/sp-multi-select";
import { Spinner } from "components/ui/spinner";
import { useUserOptionsForRegion } from "components/ui/use-user-options";
import { useGetUsersForRegionQuery } from "generated/graphql";
import React from "react";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { ContentContainer, ContentFull, PageHeader, Stage } from "../../layout";
import { trpc } from "../../rpc/trpc";

type RouterOutput = inferRouterOutputs<AppRouter>;
type TimeInStageData = RouterOutput["timeInStage"]["timeInStage"];
type StageInfo = TimeInStageData["stages"][number];
type AdvisorInfo = TimeInStageData["advisors"][number];

const ADVISOR_COLORS = [
    "hsl(200, 92%, 40%)",
    "hsl(270, 60%, 55%)",
    "hsl(30, 80%, 45%)",
    "hsl(330, 60%, 45%)",
    "hsl(240, 55%, 55%)",
    "hsl(190, 70%, 35%)",
    "hsl(350, 65%, 50%)",
    "hsl(45, 75%, 42%)",
];
const TEAM_AVG_COLOR = "#9ca3af";

const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

function formatRangeSubtitle(startDate: CalendarDate, endDate: CalendarDate): string {
    const startMonth = MONTH_NAMES[startDate.month - 1];
    const endMonth = MONTH_NAMES[endDate.month - 1];

    if (
        startDate.month === 1 &&
        startDate.day === 1 &&
        endDate.month === 12 &&
        endDate.day === 31 &&
        startDate.year === endDate.year
    ) {
        return `Jan\u2013Dec ${startDate.year}`;
    }

    if (startDate.year === endDate.year) {
        return `${startMonth} ${startDate.day}\u2013${endMonth} ${endDate.day}, ${startDate.year}`;
    }

    return `${startMonth} ${startDate.day}, ${startDate.year}\u2013${endMonth} ${endDate.day}, ${endDate.year}`;
}

function computeInsights(stages: StageInfo[], advisor: AdvisorInfo | null): string[] {
    const insights: string[] = [];

    if (advisor) {
        let bestStage: { name: string; delta: number } | null = null;
        let slowestStage: { name: string; delta: number } | null = null;

        for (const stage of stages) {
            const duration = advisor.stageDurations.find(
                d => d.stageId === stage.stageId,
            );
            if (
                duration?.avgDays == null ||
                stage.teamAvgDays == null ||
                stage.teamAvgDays === 0
            ) {
                continue;
            }

            const delta = duration.avgDays - stage.teamAvgDays;

            if (!bestStage || delta < bestStage.delta) {
                bestStage = { name: stage.stageName, delta };
            }
            if (!slowestStage || delta > slowestStage.delta) {
                slowestStage = { name: stage.stageName, delta };
            }
        }

        if (bestStage && bestStage.delta < 0) {
            insights.push(
                `Best stage: ${bestStage.name} \u2014 ${Math.abs(bestStage.delta).toFixed(1)} faster than team average.`,
            );
        }
        if (slowestStage && slowestStage.delta > 0) {
            insights.push(
                `Slowest stage: ${slowestStage.name} \u2014 ${slowestStage.delta.toFixed(1)} slower than team average.`,
            );
        }
    } else {
        const stagesWithData = stages.filter(s => s.teamAvgDays !== null);
        const totalDays = stagesWithData.reduce(
            (sum, s) => sum + (s.teamAvgDays ?? 0),
            0,
        );
        if (totalDays === 0) return [];

        const sorted = [...stagesWithData].sort(
            (a, b) => (b.teamAvgDays ?? 0) - (a.teamAvgDays ?? 0),
        );

        let cumulative = 0;
        const majorityStages: string[] = [];
        for (const stage of sorted) {
            cumulative += stage.teamAvgDays ?? 0;
            majorityStages.push(stage.stageName);
            if (cumulative / totalDays >= 0.5) break;
        }

        if (majorityStages.length === 1) {
            const pct = Math.round(((sorted[0].teamAvgDays ?? 0) / totalDays) * 100);
            insights.push(
                `${majorityStages[0]} accounts for ${pct}% of total pipeline time.`,
            );
        } else if (majorityStages.length > 1) {
            const pct = Math.round((cumulative / totalDays) * 100);
            insights.push(
                `${majorityStages.join(" and ")} together account for ${pct}% of total pipeline time.`,
            );
        }
    }

    return insights;
}

export const TimeInStage: React.FC = () => {
    const [dateRange, setDateRange] = React.useState<DateRangeValue>(() => {
        const presets = getDatePresets();
        const defaultRange = presets[0].getRange();
        return {
            startDate: defaultRange.start,
            endDate: defaultRange.end,
            activePreset: presets[0].label,
        };
    });
    const [selectedUserIds, setSelectedUserIds] = React.useState<UserId[]>([]);
    const regionId = useRegionId();
    const { startDate, endDate } = dateRange;
    const validQuery = regionId && startDate && endDate;
    const trpcClient = trpc.useTRPC();

    const query = useQuery({
        ...trpcClient.timeInStage.timeInStage.queryOptions({
            startDate: startDate?.toString() ?? "",
            endDate: endDate?.toString() ?? "",
            regionId: regionId ?? "",
        }),
        enabled: !!validQuery,
    });

    const regionUsersQuery = useGetUsersForRegionQuery(
        { regionId: regionId! },
        { ...stableQueryOptions(), enabled: !!regionId },
    );

    const userNameMap = React.useMemo(() => {
        const map = new Map<string, string>();
        for (const u of regionUsersQuery.data?.getUsersForRegion ?? []) {
            map.set(u.id, u.preferredName);
        }
        return map;
    }, [regionUsersQuery.data]);

    const advisorOptions = useUserOptionsForRegion(regionId);

    const selectedAdvisors = React.useMemo(() => {
        if (!query.data || selectedUserIds.length === 0) return [];
        return selectedUserIds.map(id => {
            const found = query.data.advisors.find(a => a.userId === id);
            if (found) return found;
            return {
                userId: id,
                displayName: userNameMap.get(id) ?? "Unknown",
                totalClientCount: 0,
                lowVolume: true,
                stageDurations: query.data.stages.map(s => ({
                    stageId: s.stageId,
                    stageKey: s.stageKey,
                    avgDays: null as number | null,
                })),
            } as AdvisorInfo;
        });
    }, [selectedUserIds, query.data, userNameMap]);

    const subtitleWho =
        selectedAdvisors.length === 1
            ? selectedAdvisors[0].displayName
            : selectedAdvisors.length > 1
              ? `${selectedAdvisors.length} advisors`
              : "All advisors";

    const subtitle = `Average days in stage before advancing \u00B7 ${subtitleWho} \u00B7 ${formatRangeSubtitle(startDate, endDate)}`;

    return (
        <Stage>
            <PageHeader title="Time in Stage" stackBreakpoint="md">
                <DateRangeControl value={dateRange} onChange={setDateRange} />
            </PageHeader>
            <div className="px-4 pb-2 -mt-1 text-sm text-gray-500">{subtitle}</div>
            <div className="px-4 py-3 mb-4 border-b border-gray-200">
                <div className="flex flex-wrap items-end gap-3">
                    {regionId ? (
                        <div className="w-full sm:w-128">
                            <SpMultiSelect
                                label="Advisor"
                                placeholder="Team Average"
                                options={advisorOptions}
                                value={selectedUserIds}
                                onChange={setSelectedUserIds}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
            <ContentContainer>
                {query.isLoading ? (
                    <ContentFull>
                        <Spinner />
                    </ContentFull>
                ) : query.error ? (
                    <ContentFull>
                        <p className="text-red-600">
                            {(query.error as { message?: string })?.message ??
                                "Failed to load report"}
                        </p>
                    </ContentFull>
                ) : !query.data || query.data.stages.length === 0 ? (
                    <ContentFull>
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-lg font-medium">No data available</p>
                            <p className="mt-1 text-sm">
                                No completed clients found in the selected date range.
                            </p>
                        </div>
                    </ContentFull>
                ) : (
                    <StageBarChart
                        stages={query.data.stages}
                        advisors={selectedAdvisors}
                    />
                )}
            </ContentContainer>
        </Stage>
    );
};

interface AdvisorBar {
    displayName: string;
    avgDays: number | null;
    color: string;
}

const StageBarChart: React.FC<{
    stages: StageInfo[];
    advisors: AdvisorInfo[];
}> = ({ stages, advisors }) => {
    const hasAdvisors = advisors.length > 0;

    const maxDays = React.useMemo(() => {
        let max = 0;
        for (const stage of stages) {
            max = Math.max(max, stage.teamAvgDays ?? 0);
            for (const advisor of advisors) {
                const d = advisor.stageDurations.find(sd => sd.stageId === stage.stageId);
                max = Math.max(max, d?.avgDays ?? 0);
            }
        }
        return Math.max(max, 0.01);
    }, [stages, advisors]);

    const singleAdvisor = advisors.length === 1 ? advisors[0] : null;
    const insights = React.useMemo(
        () => computeInsights(stages, singleAdvisor),
        [stages, singleAdvisor],
    );

    const lowVolumeAdvisors = advisors.filter(a => a.lowVolume);

    return (
        <div className="space-y-4">
            {lowVolumeAdvisors.length > 0 ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                    {lowVolumeAdvisors.length === 1
                        ? `${lowVolumeAdvisors[0].displayName} has fewer than 3 completed clients in this period. Results may not be representative.`
                        : `${lowVolumeAdvisors.map(a => a.displayName).join(", ")} each have fewer than 3 completed clients in this period. Results may not be representative.`}
                </div>
            ) : null}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {hasAdvisors ? (
                    <>
                        {advisors.map((advisor, i) => (
                            <span
                                key={advisor.userId}
                                className="flex items-center gap-1.5">
                                <span
                                    className="inline-block w-3 h-3 rounded-sm"
                                    style={{
                                        backgroundColor:
                                            ADVISOR_COLORS[i % ADVISOR_COLORS.length],
                                    }}
                                />
                                {advisor.displayName}
                            </span>
                        ))}
                        <span className="flex items-center gap-1.5">
                            <span
                                className="inline-block w-3 h-3 rounded-sm"
                                style={{ backgroundColor: TEAM_AVG_COLOR }}
                            />
                            Team average
                        </span>
                    </>
                ) : (
                    <span className="flex items-center gap-1.5">
                        <span
                            className="inline-block w-3 h-3 rounded-sm"
                            style={{ backgroundColor: ADVISOR_COLORS[0] }}
                        />
                        Team average
                    </span>
                )}
            </div>
            <div className={`overflow-hidden ${defaultCardStyle}`}>
                {stages.map((stage, i) => {
                    const bars: AdvisorBar[] = advisors.map((advisor, ai) => ({
                        displayName: advisor.displayName,
                        avgDays:
                            advisor.stageDurations.find(
                                sd => sd.stageId === stage.stageId,
                            )?.avgDays ?? null,
                        color: ADVISOR_COLORS[ai % ADVISOR_COLORS.length],
                    }));

                    return (
                        <StageRow
                            key={stage.stageId}
                            stage={stage}
                            advisorBars={bars}
                            maxDays={maxDays}
                            index={i}
                        />
                    );
                })}
            </div>
            {insights.length > 0 ? <InsightStrip insights={insights} /> : null}
            <div className="px-1 text-xs text-gray-500 italic">
                Stage averages only include clients who passed through that stage. Clients
                who skipped a stage are not included in its average.
            </div>
        </div>
    );
};

function daysColorClass(days: number | null, teamDays: number | null): string {
    if (days === null || teamDays === null || teamDays === 0) {
        return "text-zinc-700";
    }
    const pct = ((days - teamDays) / teamDays) * 100;
    if (pct < -5) return "text-emerald-700";
    if (pct > 5) return "text-red-600";
    return "text-zinc-700";
}

const StageRow: React.FC<{
    stage: StageInfo;
    advisorBars: AdvisorBar[];
    maxDays: number;
    index: number;
}> = ({ stage, advisorBars, maxDays, index }) => {
    const teamDays = stage.teamAvgDays;
    const hasAdvisors = advisorBars.length > 0;
    const bgClass = index % 2 === 0 ? "bg-white" : "bg-gray-50";
    const borderClass = index > 0 ? "border-t border-gray-200" : "";

    if (!hasAdvisors) {
        const teamBarWidth =
            teamDays !== null && maxDays > 0 ? (teamDays / maxDays) * 100 : 0;
        return (
            <div className={`px-4 py-2.5 ${bgClass} ${borderClass}`}>
                <div className="text-sm font-medium text-zinc-950 mb-1">
                    {stage.stageName}
                </div>
                <div className="flex items-center gap-3 pl-2">
                    <div className="w-32 md:w-40 flex-shrink-0" />
                    <div className="w-12 text-right flex-shrink-0">
                        {teamDays !== null ? (
                            <span className="text-sm font-medium tabular-nums text-zinc-700">
                                {teamDays.toFixed(1)}
                            </span>
                        ) : (
                            <span className="text-sm text-gray-400">{"\u2014"}</span>
                        )}
                    </div>
                    <div className="flex-1 max-w-36">
                        <div className="h-2.5 bg-gray-100 rounded overflow-hidden">
                            {teamDays !== null ? (
                                <div
                                    className="h-full rounded"
                                    style={{
                                        width: `${teamBarWidth}%`,
                                        backgroundColor: ADVISOR_COLORS[0],
                                        minWidth: teamDays > 0 ? 3 : 0,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`px-4 py-3 ${bgClass} ${borderClass}`}>
            <div className="text-sm font-medium text-zinc-950 mb-1.5">
                {stage.stageName}
            </div>
            <div className="space-y-1 pl-2">
                {advisorBars.map(bar => {
                    const barWidth =
                        bar.avgDays !== null && maxDays > 0
                            ? (bar.avgDays / maxDays) * 100
                            : 0;
                    const colorClass = daysColorClass(bar.avgDays, teamDays);
                    return (
                        <div key={bar.displayName} className="flex items-center gap-3">
                            <div className="w-32 md:w-40 flex-shrink-0 text-sm text-gray-600 truncate">
                                {bar.displayName}
                            </div>
                            <div className="w-12 text-right flex-shrink-0">
                                {bar.avgDays !== null ? (
                                    <span
                                        className={`text-sm font-medium tabular-nums ${colorClass}`}>
                                        {bar.avgDays.toFixed(1)}
                                    </span>
                                ) : (
                                    <span className="text-sm text-gray-400">
                                        {"\u2014"}
                                    </span>
                                )}
                            </div>
                            <div className="flex-1 max-w-36">
                                <div className="h-2.5 bg-gray-100 rounded overflow-hidden">
                                    {bar.avgDays !== null ? (
                                        <div
                                            className="h-full rounded"
                                            style={{
                                                width: `${barWidth}%`,
                                                backgroundColor: bar.color,
                                                minWidth: bar.avgDays > 0 ? 3 : 0,
                                            }}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className="flex items-center gap-3 pt-0.5 border-t border-gray-100">
                    <div className="w-32 md:w-40 flex-shrink-0 text-sm text-gray-400">
                        Team avg
                    </div>
                    <div className="w-12 text-right flex-shrink-0">
                        {teamDays !== null ? (
                            <span className="text-sm tabular-nums text-gray-400">
                                {teamDays.toFixed(1)}
                            </span>
                        ) : (
                            <span className="text-sm text-gray-400">{"\u2014"}</span>
                        )}
                    </div>
                    <div className="flex-1 max-w-36">
                        <div className="h-2.5 bg-gray-100 rounded overflow-hidden">
                            {teamDays !== null ? (
                                <div
                                    className="h-full rounded"
                                    style={{
                                        width: `${(teamDays / maxDays) * 100}%`,
                                        backgroundColor: TEAM_AVG_COLOR,
                                        minWidth: teamDays > 0 ? 3 : 0,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const InsightStrip: React.FC<{ insights: string[] }> = ({ insights }) => {
    return (
        <div
            className={`rounded-lg px-4 py-3 ${defaultCardStyle} bg-blue-50 border-blue-200`}>
            <h3 className="text-xs font-medium text-blue-700 mb-2">Insights</h3>
            <ul className="space-y-1">
                {insights.map(insight => (
                    <li key={insight} className="text-sm text-blue-900">
                        {insight}
                    </li>
                ))}
            </ul>
        </div>
    );
};
